/**
 * **Confirm payment** — staff saw the money arrive (C25, FR-J11 item 9,
 * [ADR-0052](../../../../../../docs/adr/0052-a-booking-may-be-paid-by-bank-qr-and-staff-confirm-the-payment.md)
 * §4; Wave 27B, T-5.74). Laid out as every staff dialog is (`FORM_GRID` and
 * `FormRow`, C14 §10.6 item 7).
 *
 * Three fields, each prefilled:
 *
 * - **how it came** — the manual receipt modes; **UPI** for a bank-QR booking,
 *   **bank transfer** for a gateway one (ADR-0052 §4);
 * - **the reference** — the one the visitor typed beside their proof;
 * - **the date it was received** — today, and never after it.
 *
 * What pressing it does is said **before** it is pressed: an invoice issued
 * from the amount the visitor was shown (when none exists), a receipt for what
 * is outstanding, the booking confirmed and its emails sent. **Step-up is asked
 * only when an invoice will be written** — the service says which
 * (`allowed.confirm_issues_invoice`), and a gateway booking whose invoice
 * already exists confirms without it. The kit's dialog steps aside while the
 * step-up is open, as `RefundDialog` does: two kit dialogs at once each read a
 * click in the other as an outside click.
 */

import { Alert, Button, Dialog, Input, Select } from '@fiveox/ui'
import { useState } from 'react'

import { StepUpDialog } from '../../../components/StepUpDialog'
import { FORM_GRID, FormRow } from '../../../components/page'
import { ApiError } from '../../../lib/api'
import {
  MANUAL_RECEIPT_MODES,
  MODE_LABELS,
  type ManualReceiptMode,
  iso,
  rupees,
} from '../../../lib/api/billing'
import { type BookingDetail, booking } from '../../../lib/api/booking'
import { REFERENCE_LABEL } from '../../Billing/RecordReceiptDialog'
import { DialogFooter } from '../../Billing/shared'

/** ADR-0052 §4 — UPI for a bank-QR booking, bank transfer for a gateway one. */
export function defaultMode(row: Pick<BookingDetail, 'flow'>): ManualReceiptMode {
  return row.flow.payment_mode === 'qr' ? 'upi' : 'bank_transfer'
}

/** Whether confirming writes an invoice — the service's answer, else (a
 *  service before 27B) whether the booking has none yet. */
export function issuesInvoice(row: Pick<BookingDetail, 'allowed' | 'invoice_id'>): boolean {
  const said = (row.allowed as { confirm_issues_invoice?: boolean } | undefined)?.confirm_issues_invoice
  return typeof said === 'boolean' ? said : !row.invoice_id
}

/** What is being received: what is still owed on the invoice, else the
 *  amount the visitor was shown. */
export function amountDue(row: Pick<BookingDetail, 'payment' | 'amount'>): string | null {
  return row.payment.outstanding ?? row.amount ?? null
}

/** The sentence under the form — what Confirm writes, before it is pressed. */
export function confirmEffect(row: BookingDetail): string {
  const due = amountDue(row)
  const money = due ? rupees(due) : 'the amount the visitor was shown'
  const emails = 'and the booking is confirmed — the client is emailed the confirmation and the receipt.'
  if (issuesInvoice(row)) {
    return `A tax invoice for ${money} is issued from the price the visitor was shown, a receipt for it is recorded, ${emails}`
  }
  const invoice = row.invoice_number ? `invoice ${row.invoice_number}` : 'its invoice'
  return `A receipt for the ${money} still owed on ${invoice} is recorded, ${emails}`
}

export function ConfirmPaymentDialog({
  row,
  onClose,
  onConfirmed,
}: {
  row: BookingDetail
  onClose: () => void
  onConfirmed: (row: BookingDetail) => void
}) {
  const [mode, setMode] = useState<ManualReceiptMode>(() => defaultMode(row))
  const [reference, setReference] = useState(row.payment_reference ?? '')
  const [received, setReceived] = useState(iso(new Date()))
  const [verifying, setVerifying] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const today = iso(new Date())
  const writesInvoice = issuesInvoice(row)

  const send = async (assertion?: string) => {
    setBusy(true)
    setError(null)
    try {
      const resp = await booking.confirmPayment(
        row.id,
        { mode, reference: reference.trim() || null, received_on: received },
        assertion,
      )
      onConfirmed(resp.data)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'The payment could not be confirmed.')
      setVerifying(false)
      setBusy(false)
    }
  }

  // Why Confirm is not ready yet, in the firm's words — a disabled control
  // says why (the Wave 15 design bar).
  const why = !received
    ? 'Choose the date the money was received.'
    : received > today
      ? 'The money cannot have been received after today.'
      : reference.trim().length > 120
        ? 'A reference is at most 120 characters.'
        : undefined

  return (
    <>
      <Dialog open={!verifying} onClose={onClose} title="Confirm payment" error={error} width="lg">
        <div className={FORM_GRID}>
          <FormRow label="Received by" htmlFor="confirm-mode">
            <Select
              id="confirm-mode"
              value={mode}
              onChange={(e) => setMode(e.target.value as ManualReceiptMode)}
            >
              {MANUAL_RECEIPT_MODES.map((m) => (
                <option key={m} value={m}>
                  {MODE_LABELS[m]}
                </option>
              ))}
            </Select>
          </FormRow>
          <FormRow label={REFERENCE_LABEL[mode]} htmlFor="confirm-reference" optional>
            <Input
              id="confirm-reference"
              value={reference}
              maxLength={120}
              onChange={(e) => setReference(e.target.value)}
              hint={
                row.payment_reference
                  ? 'The reference the visitor sent with their payment proof.'
                  : 'The UTR or transaction ID, if you have one.'
              }
            />
          </FormRow>
          <FormRow label="Received on" htmlFor="confirm-received">
            <Input
              id="confirm-received"
              type="date"
              value={received}
              max={today}
              onChange={(e) => setReceived(e.target.value)}
            />
          </FormRow>
        </div>

        <Alert tone="info" className="mt-5">
          <span data-confirm-effect="">{confirmEffect(row)}</span>
        </Alert>

        <DialogFooter onCancel={onClose} busy={busy}>
          <span title={why}>
            <Button
              onClick={() => (writesInvoice ? setVerifying(true) : void send())}
              disabled={busy || why !== undefined}
            >
              {writesInvoice ? 'Continue' : 'Confirm payment'}
            </Button>
          </span>
        </DialogFooter>
      </Dialog>

      <StepUpDialog
        open={verifying}
        title="Confirm this payment"
        what="A tax invoice is issued, and takes the next number in your series — it cannot be undone from here, only reversed by a credit note. A receipt is recorded against it and the booking is confirmed."
        onClose={() => setVerifying(false)}
        onVerified={send}
      />
    </>
  )
}
